<script lang="ts">
import { defineComponent } from 'vue';
import { useBetsStore } from '@/stores/bets';
import { mapState } from 'pinia';
import { BetSlipResultStatus, SystemBetTypesEnum } from 'shared-types';
import { BetSlipRejectStatus } from '@/types/coupons.enum';
import { formatCurrency, formatDate, formatMoney } from '@/utils/format';
import { SystemBetTypesLimitations } from '@/types/bets';
import ClipboardIcon from '@/components/clipboard-icon.vue';
import Decimal from 'decimal.js-light';
import BetsRowTotals from '@/components/bets/bets-row-totals.vue';
import { getIsBetCasheableFlag, isBetCasheable } from '@/components/calculator/helper';

export default defineComponent({
  name: 'bets',
  components: { BetsRowTotals, ClipboardIcon },
  computed: {
    SystemBetTypesEnum() {
      return SystemBetTypesEnum;
    },
    BetSlipRejectStatus() {
      return BetSlipRejectStatus;
    },
    BetSlipResultStatus() {
      return BetSlipResultStatus;
    },
    ...mapState(useBetsStore, ['loading', 'offset']),
    totalPayout() {
      return new Decimal(this.totals?.paidOutAmount || 0).plus(
        new Decimal(this.totals?.refundAmount || 0)
      );
    },
    totalBalance() {
      return new Decimal(this.totals?.stakeAmount || 0).minus(this.totalPayout);
    },
  },
  data() {
    return {
      SystemBetTypesLimitations,
    };
  },
  methods: {
    getIsBetCasheableFlag,
    formatCurrency,
    formatMoney,
    formatDate,
    getDiffIcon(stake: any) {
      const diff = new Decimal(stake.selection.Decimal).minus(stake.odds).toNumber();
      if (stake.selection.ResultStatus === 'Winner') return 'pi-plus text-green-500';
      if (stake.selection.ResultStatus === 'Partial' || stake.selection.ResultStatus === 'Placed') return 'pi-plus text-yellow-500';
      if (+stake.selection.Decimal < 1.001) return 'pi-ellipsis-v text-yellow-500';
      if (diff > 1) return 'pi-angle-double-down text-red-500';
      if (diff > 0) return 'pi-angle-down text-red-500';
      if (diff < -1) return 'pi-angle-double-up text-green-500';
      if (diff < 0) return 'pi-angle-up text-green-500';
      return 'pi-ellipsis-v';
    },
    lastDate(bets: any[]) {
      let maxDate: string = '';

      for (let i = 0; i < bets.length; i += 1) {
        const date = bets[i].selection.Market.Fixture.StartTimeUtc;
        if (date > maxDate) {
          maxDate = date;
        }
      }

      return maxDate;
    },
    sortedStakes(bets: any[]) {
      return bets.sort((a: any, b: any) => {
        if (a.selection.Market.Fixture.StartTimeUtc > b.selection.Market.Fixture.StartTimeUtc) return 1;
        if (a.selection.Market.Fixture.StartTimeUtc < b.selection.Market.Fixture.StartTimeUtc) return -1;
        return 0;
      })
    },
    isCashoutEnabled(bets: any[]) {
      for (const bet of bets) {
        // FIXME for bets table cash out based on betSlip.Selections
        const betSlipSelection = this.betSlipSelections;
        if (!isBetCasheable(bet, betSlipSelection)) return false;
      }

      return true;
    },
  },
  props: ['records', 'totals', 'filterUser', 'filterCurrency', 'pageSize'],
});
</script>

<template>
  <section class="relative mx-3">
    <div v-if="loading" class="table-loader">
      <i class="pi pi-spinner animate-spin text-gray-200" style="font-size: 5em" />
    </div>
    <table class="basic-table large">
      <thead>
        <tr>
          <th rowspan="2">&#8470;</th>
          <th rowspan="2">Дата</th>
          <th rowspan="2">GUID</th>
          <th rowspan="2">Игрок</th>
          <th rowspan="2">Промо</th>
          <th rowspan="2">Статус</th>
          <th rowspan="2">Тип</th>

          <th colspan="4">Валюта игрока</th>

          <th colspan="2">Макс. Выигрыш</th>
          <th rowspan="2">RM</th>

          <th colspan="3">USD</th>
        </tr>
        <tr>
          <th>Валюта</th>
          <th>Ставка</th>
          <th>Выплата</th>
          <th>Возврат</th>

          <th>Валюта</th>
          <th>USD</th>

          <th>Ставка</th>
          <th>Выплата</th>
          <th>Возврат</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="(bet, i) in records" :key="bet.id">
          <td>
            <span class="text-gray-500">{{ bet.parentBetSlipId }}</span>
            {{ bet.id }}
          </td>
          <td>
            {{ formatDate(bet.createdAt, 'DD.MM.YYYY HH:mm:ss') }}
          </td>
          <td class="text-nowrap">
            <router-link
              :to="{
                name: 'calculator-item',
                params: { id: bet.parentBetSlipGuid, stakeId: bet.guid },
              }"
              class="text-blue-500 underline font-mono"
            >
              {{ bet.guid }}
            </router-link>
            <clipboard-icon :text="bet.guid" class="ml-1" iconClass="text-sm"/>
          </td>
          <td class="text-nowrap">
            <span class="text-blue-500 underline cursor-pointer" @click="filterUser(bet.userId)">
              {{ bet.userId }}
            </span>
            <clipboard-icon :text="bet.userId" class="ml-1" iconClass="text-xs" />
            <i
              class="pi pi-external-link ml-1 text-xs opacity-40 hover:opacity-100 cursor-pointer"
              @click="$router.push({ name: 'profile', params: { id: bet.userId }})"
            />
          </td>
          <td>{{ bet.promo?.split('#').reverse().join(' : ') }}</td>
          <td class="no-wrap text-nowrap relative z-1">
            <div
              :class="{
                'text-green-500': bet.status === BetSlipResultStatus.Won,
                'text-yellow-500': bet.status === BetSlipResultStatus.Lost,
                'text-blue-500': bet.status === BetSlipResultStatus.Voided && !+bet.payout,
                'text-violet-500': bet.status === BetSlipResultStatus.Voided && +bet.payout,
                'text-red-500': bet.rejectStatus === BetSlipRejectStatus.Rejected,
              }"
            >
              <template v-if="bet.status === 'Voided' && +bet.payout">
                Cashout
              </template>
              <template v-else-if="bet.status || bet.rejectStatus">
                {{ bet.status || bet.rejectStatus }}
              </template>
              <template v-else>
                <div class="hoverable">
                  <span :class="{'text-green-500': isCashoutEnabled(bet.bets) }">
                    {{ formatDate(lastDate(bet.bets),'DD.MM.YYYY') }}
                  </span>
                  <div :class="{
                    'absolute left-0 bg-gray-200 dark:bg-gray-800 border z-10': true,
                    'top-full': i < pageSize / 2,
                    'bottom-full': i >= pageSize / 2,
                  }">
                    <table>
                      <thead>
                        <tr>
                          <th>Дата</th>
                          <th>
                            <i class="pi pi-trophy" />
                          </th>
                          <th>Спорт</th>
                          <th>Матч</th>
                          <th>Маркет</th>
                          <th>Исход</th>
                          <th>Коэффициент</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="stake in sortedStakes(bet.bets)">
                          <td>{{ formatDate(stake.selection.Market.Fixture.StartTimeUtc, 'DD.MM.YYYY HH:mm') }}</td>
                          <td>
                            <i class="pi pi-check text-green-500" v-if="stake.selection.ResultStatus === 'Winner'"/>
                            <i class="pi pi-refresh text-yellow-500" v-if="stake.selection.ResultStatus === 'Pushed'"/>
                            <i class="pi pi-percentage text-yellow-500" v-if="stake.selection.ResultStatus === 'Placed'"/>
                            <i class="pi pi-percentage text-yellow-500" v-if="stake.selection.ResultStatus === 'Partial'"/>
                          </td>
                          <td class="!text-left">{{ stake.selection.Market.Fixture.Sport.Name }}</td>
                          <td class="!text-left">{{ stake.selection.Market.Fixture.Name }}</td>
                          <td class="!text-left">{{ stake.selection.Market.Name }}</td>
                          <td :class="{
                            '!text-left': true,
                            'text-green-500': stake.selection.ResultStatus
                          }">{{ stake.selection.Name }}</td>
                          <td class="!font-mono">
                            <span>{{ formatMoney(stake.odds, 2, false) }}</span>
                            &hyphen;
                            <span>{{
                              stake.selection.Decimal === '0'
                               ? '0.00'
                               : formatMoney(stake.selection.Decimal, 2, false)
                            }}</span>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </template>
            </div>
          </td>
          <td class="text-nowrap">
            {{ bet.type }}
            <span class="text-gray-500" v-if="bet.bets.length > 1">
              [{{ bet.bets.length }}]
            </span>
            <template v-if="!bet.status && !bet.rejectStatus">
              <br v-if="bet.bets.length > 1"/>
              <i

                v-for="stake in bet.bets"
                :class="{
                'pi text-xs': true,
                [getDiffIcon(stake)]: true,
              }"
              />
            </template>
          </td>
          <td>
            <span
              class="text-blue-500 underline cursor-pointer"
              @click="filterCurrency(bet.currency)"
            >
              {{ formatCurrency(bet.stakeAmount, bet.currency) }}
            </span>
          </td>
          <td class="!text-right font-mono">
            {{ formatMoney(bet.stakeAmount, 4) }}
          </td>
          <td class="!text-right font-mono !text-opacity-70">{{ formatMoney(bet.payout, 4) }}</td>
          <td class="!text-right font-mono !text-opacity-70">{{ formatMoney(bet.refund, 4) }}</td>

          <td class="!text-right font-mono !text-opacity-70">{{ formatMoney(bet.maxPayout, 4) }}</td>
          <td class="!text-right font-mono">{{ formatMoney(bet.vendorMaxPayout, 4) }}</td>

          <td class="!text-right font-mono !text-opacity-70">
            {{
              formatMoney(
                ((bet.maxPayout - bet.stakeAmount) * bet.vendorStakeAmount) / bet.stakeAmount,
                2
              )
            }}
          </td>

          <td class="!text-right font-mono">
            {{ formatMoney(bet.vendorStakeAmount, 4, false) }}
          </td>

          <td class="!text-right font-mono">
            {{ formatMoney((bet.payout * bet.vendorStakeAmount) / bet.stakeAmount, 4, false) }}
          </td>
          <td class="!text-right font-mono">
            {{ formatMoney((bet.refund * bet.vendorStakeAmount) / bet.stakeAmount, 4, false) }}
          </td>
        </tr>
        <bets-row-totals v-if="totals" :totals="totals" />
      </tbody>
    </table>
  </section>
</template>
<style lang="scss" scoped>
.large td {
  @apply text-sm p-3;
}

.hoverable {
  div { display: none; };
  &:hover {
    div { display: block; }
  }
}
</style>
